import { type ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import {
    Box, Flex, Text, IconButton, Icon, Menu, MenuButton, MenuList, MenuItem, Tooltip, Spinner
} from '@chakra-ui/react';
import { TbDotsVertical, TbEye, TbPencil, TbDownload, TbTrash, TbInfoCircle, TbSettings } from 'react-icons/tb';
import ReportingFullScreenShell from '../components/ReportingFullScreenShell';
import {
    ReportingChartFullscreenButton,
    ReportingChartFullscreenProvider,
} from '../shared/reportingChartFullscreen';
import {
    STANDARD_CHART_HEIGHT,
    WIDGET_GRID_GAP_PX,
    WIDGET_SPAN_STEPS,
    clampGridSpan,
    clampWidgetHeight,
    type ResizeCorner,
    type WidgetGridSize,
} from './types';
import { cornerCursor, eligibleResizeCorners, resizeFromCorner } from './widgetDashboardLayout';
import { efficiencyBreakdownTooltipLabel, EFFICIENCY_BREAKDOWN_TOOLTIP } from '../../../shared/efficiencyBreakdownInfo';

interface WidgetCardProps {
    title: string;
    description?: string;
    controls?: ReactNode;
    children: ReactNode;
    onView?: () => void;
    onEdit?: () => void;
    onDownload?: () => void;
    onDelete?: () => void;
    isKeyMetric?: boolean;
    noPadding?: boolean;
    dragHandleProps?: Record<string, any>;
    onMovePointerDown?: (event: React.PointerEvent) => void;
    isMoving?: boolean;
    onResize?: (next: WidgetGridSize) => void;
    onResizePreview?: (next: WidgetGridSize | null) => void;
    currentSpan?: number;
    currentHeight?: number;
    currentGridX?: number;
    currentGridY?: number;
    /** Background refetch with existing data — dims chart content and shows header spinner. */
    isRefetching?: boolean;

}

function descriptionTooltipLabel(description: string): ReactNode {
    if (description === EFFICIENCY_BREAKDOWN_TOOLTIP) return efficiencyBreakdownTooltipLabel();
    const paragraphs = description.split(/\n\n+/);
    if (paragraphs.length === 1) return description;
    return (
        <>
            {paragraphs.map((paragraph, index) => (
                <Box as="p" key={index} mt={index === 0 ? 0 : 2} mb={0}>
                    {paragraph}
                </Box>
            ))}
        </>
    );
}

const OUTLINE_ICON_BTN = {
    size: 'sm' as const,
    h: '32px',
    w: '32px',
    variant: 'outline' as const,
    borderColor: 'gray.300',
    color: 'gray.600',
    borderRadius: 'md',
    _hover: { bg: 'gray.50' },
    _active: { bg: 'gray.100' },
};

const WidgetFullscreenToolbar = ({
    onEdit,
    onDownload,
    isFullScreen,
    onToggleFullscreen,
}: {
    onEdit?: () => void;
    onDownload?: () => void;
    isFullScreen: boolean;
    onToggleFullscreen: () => void;
}) => (
    <Flex align="center" gap={2} flexShrink={0}>
        {onEdit && (
            <Tooltip label="Widget settings" placement="top" hasArrow>
                <IconButton
                    aria-label="Widget settings"
                    icon={<Icon as={TbSettings as React.ElementType} boxSize={4} />}
                    onClick={onEdit}
                    {...OUTLINE_ICON_BTN}
                />
            </Tooltip>
        )}
        {onDownload && (
            <Tooltip label="Export to CSV" placement="top" hasArrow>
                <IconButton
                    aria-label="Export to CSV"
                    icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                    onClick={onDownload}
                    {...OUTLINE_ICON_BTN}
                />
            </Tooltip>
        )}
        <ReportingChartFullscreenButton
            isFullScreen={isFullScreen}
            onToggle={onToggleFullscreen}
        />
    </Flex>
);

const WidgetMenuItems = ({ onView, onEdit, onDownload, onDelete }: Pick<WidgetCardProps, 'onView' | 'onEdit' | 'onDownload' | 'onDelete'>) => {
    const hasMenu = onView || onEdit || onDownload || onDelete;
    if (!hasMenu) return null;
    return (
        <Menu placement="bottom-end">
            <MenuButton
                as={IconButton}
                icon={<Icon as={TbDotsVertical as React.ElementType} boxSize={4} />}
                aria-label="Widget options"
                size="sm"
                h="32px"
                w="32px"
                variant="outline"
                borderColor="gray.300"
                color="gray.600"
                borderRadius="md"
                _hover={{ bg: 'gray.50' }}
                _active={{ bg: 'gray.100' }}
            />
            <MenuList py={1} minW="160px" zIndex={1500}>
                {onView && <MenuItem icon={<Icon as={TbEye as React.ElementType} />} fontSize="sm" onClick={onView}>View</MenuItem>}
                {onEdit && <MenuItem icon={<Icon as={TbPencil as React.ElementType} />} fontSize="sm" onClick={onEdit}>Edit</MenuItem>}
                {onDownload && <MenuItem icon={<Icon as={TbDownload as React.ElementType} />} fontSize="sm" onClick={onDownload}>Download</MenuItem>}
                {onDelete && <MenuItem icon={<Icon as={TbTrash as React.ElementType} />} fontSize="sm" color="red.500" onClick={onDelete}>Delete</MenuItem>}
            </MenuList>
        </Menu>
    );
};

const WidgetCard = ({
    title,
    description,
    controls,
    children,
    onView,
    onEdit,
    onDownload,
    onDelete,
    isKeyMetric,
    noPadding,
    dragHandleProps,
    onMovePointerDown,
    isMoving = false,
    onResize,
    onResizePreview,
    currentSpan,
    currentHeight,
    currentGridX,
    currentGridY,
    isRefetching = false,
}: WidgetCardProps) => {
    const showHeaderSpinner = isRefetching;
    const menuProps = { onView, onEdit, onDownload, onDelete };
    const [isFullScreen, setIsFullScreen] = useState(false);

    const [previewSize, setPreviewSize] = useState<WidgetGridSize | null>(null);
    const [activeCorner, setActiveCorner] = useState<ResizeCorner | null>(null);
    const dragStateRef = useRef<{
        startX: number;
        startY: number;
        unitWidth: number;
        unitHeight: number;
        startSpan: number;
        startHeight: number;
        startGridX: number;
        startGridY: number;
        corner: ResizeCorner;
    } | null>(null);
    const previewSizeRef = useRef<WidgetGridSize | null>(null);
    const onResizeRef = useRef(onResize);
    onResizeRef.current = onResize;
    const onResizePreviewRef = useRef(onResizePreview);
    onResizePreviewRef.current = onResizePreview;
    const cleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        return () => { cleanupRef.current?.(); };
    }, []);

    const handleResizePointerDown = useCallback((corner: ResizeCorner) => (e: React.PointerEvent) => {
        if (!onResizeRef.current) return;
        e.stopPropagation();
        e.preventDefault();

        const handle = e.currentTarget as HTMLElement;
        const gridContainer = handle.closest('[data-widget-grid]');
        const containerWidth = gridContainer
            ? gridContainer.getBoundingClientRect().width
            : window.innerWidth;

        const startSpan = clampGridSpan(currentSpan);
        const startHeight = clampWidgetHeight(currentHeight);
        const startGridX = currentGridX ?? 0;
        const startGridY = currentGridY ?? 0;
        const startX = e.clientX;
        const startY = e.clientY;
        const unitWidth = containerWidth / WIDGET_SPAN_STEPS;
        const unitHeight = STANDARD_CHART_HEIGHT + WIDGET_GRID_GAP_PX;
        const start = { gridX: startGridX, gridY: startGridY, gridSpan: startSpan, widgetHeight: startHeight };

        const initial = { ...start };
        dragStateRef.current = {
            startX, startY, unitWidth, unitHeight, startSpan, startHeight, startGridX, startGridY, corner,
        };
        previewSizeRef.current = initial;
        setPreviewSize(initial);
        setActiveCorner(corner);
        onResizePreviewRef.current?.(initial);

        document.body.style.cursor = cornerCursor(corner);
        document.body.style.userSelect = 'none';

        const applyPreview = (next: WidgetGridSize) => {
            const prev = previewSizeRef.current;
            if (
                prev
                && prev.gridSpan === next.gridSpan
                && prev.widgetHeight === next.widgetHeight
                && prev.gridX === next.gridX
                && prev.gridY === next.gridY
            ) return;
            previewSizeRef.current = next;
            setPreviewSize(next);
            onResizePreviewRef.current?.(next);
        };

        const handleMove = (moveEvent: PointerEvent) => {
            if (!dragStateRef.current) return;
            const { startX: sx, startY: sy, unitWidth: uw, unitHeight: uh } = dragStateRef.current;
            applyPreview(resizeFromCorner(
                start,
                corner,
                Math.round((moveEvent.clientX - sx) / uw),
                Math.round((moveEvent.clientY - sy) / uh),
            ));
        };

        const resetPreview = () => {
            dragStateRef.current = null;
            previewSizeRef.current = null;
            setPreviewSize(null);
            setActiveCorner(null);
            onResizePreviewRef.current?.(null);
        };

        const cleanup = () => {
            document.removeEventListener('pointermove', handleMove);
            document.removeEventListener('pointerup', handleUp);
            document.removeEventListener('pointercancel', handleCancel);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            cleanupRef.current = null;
        };

        const handleUp = (upEvent: PointerEvent) => {
            cleanup();
            const dx = Math.abs(upEvent.clientX - startX);
            const dy = Math.abs(upEvent.clientY - startY);
            if (dx < 5 && dy < 5 && corner === 'bottom-right') {
                const nextSpan = startSpan >= WIDGET_SPAN_STEPS ? 1 : startSpan + 1;
                onResizeRef.current?.(resizeFromCorner(start, corner, nextSpan - startSpan, 0));
            } else if (previewSizeRef.current) {
                const next = previewSizeRef.current;
                const unchanged = next.gridSpan === start.gridSpan
                    && next.widgetHeight === start.widgetHeight
                    && next.gridX === start.gridX
                    && next.gridY === start.gridY;
                if (!unchanged) onResizeRef.current?.(next);
            }
            resetPreview();
        };

        const handleCancel = () => {
            cleanup();
            resetPreview();
        };

        cleanupRef.current = cleanup;
        document.addEventListener('pointermove', handleMove);
        document.addEventListener('pointerup', handleUp);
        document.addEventListener('pointercancel', handleCancel);
    }, [currentSpan, currentHeight, currentGridX, currentGridY]);

    const displaySpan = previewSize?.gridSpan ?? currentSpan ?? 2;
    const displayHeight = previewSize?.widgetHeight ?? currentHeight ?? 1;
    const isDragResizing = previewSize !== null;
    const canMove = Boolean(onMovePointerDown) && !isFullScreen;
    const stopCardDrag = (event: React.SyntheticEvent) => event.stopPropagation();

    if (isKeyMetric) {
        return (
            <Box
                {...dragHandleProps}
                style={{ touchAction: dragHandleProps ? 'none' : undefined }}
                cursor={dragHandleProps ? 'grab' : undefined}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                shadow="sm"
                overflow="hidden"
                h="100%"
                _hover={dragHandleProps ? { borderColor: '#4051B5', boxShadow: '0 0 0 1px #4051B5' } : undefined}
                transition="border-color 0.12s, box-shadow 0.12s"
            >
                <Flex justify="space-between" align="center" px={4} py={2.5} bg="gray.50">
                    <Flex align="center" gap={1.5}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.600" noOfLines={1}>{title}</Text>
                        {showHeaderSpinner && <Spinner size="xs" color="#4051B5" flexShrink={0} />}
                        {description && (
                            <Tooltip label={descriptionTooltipLabel(description)} placement="top" hasArrow maxW="360px">
                                <Box as="span" color="gray.400" cursor="help" fontSize="xs">ⓘ</Box>
                            </Tooltip>
                        )}
                    </Flex>
                    <Box onPointerDown={stopCardDrag}>
                        <WidgetMenuItems {...menuProps} />
                    </Box>
                </Flex>
                <Box px={4} py={3} opacity={isRefetching ? 0.55 : 1} transition="opacity 0.15s">{children}</Box>
            </Box>
        );
    }

    const toggleFullScreen = () => setIsFullScreen(v => !v);

    const titleBlock = (
        <Flex align="center" gap={1.5} minW={0} flexShrink={1}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.600" noOfLines={1}>{title}</Text>
            {showHeaderSpinner && <Spinner size="xs" color="#4051B5" flexShrink={0} />}
            {description && (
                <Tooltip label={descriptionTooltipLabel(description)} placement="top" hasArrow maxW="360px">
                    <Box as="span" display="inline-flex" color="gray.400" _hover={{ color: 'gray.600' }} cursor="help" onPointerDown={stopCardDrag}>
                        <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />
                    </Box>
                </Tooltip>
            )}
        </Flex>
    );

    const cardHeader = (
        <Flex justify="space-between" align="center" w="100%" gap={2}>
            {titleBlock}
            {isFullScreen ? (
                <WidgetFullscreenToolbar
                    onEdit={onEdit}
                    onDownload={onDownload}
                    isFullScreen={isFullScreen}
                    onToggleFullscreen={toggleFullScreen}
                />
            ) : (
                <Flex align="center" gap={2} flexShrink={0} onPointerDown={stopCardDrag}>
                    {controls}
                    <ReportingChartFullscreenButton
                        isFullScreen={isFullScreen}
                        onToggle={toggleFullScreen}
                    />
                    <WidgetMenuItems {...menuProps} />
                </Flex>
            )}
        </Flex>
    );

    const chartBody = (
        <Box flex={1} minW={0} minH={0} overflow="hidden" p={noPadding ? 0 : 3} opacity={isRefetching ? 0.55 : 1} transition="opacity 0.15s">
            {children}
        </Box>
    );

    const resizeHandles = onResize && currentSpan && !isFullScreen
        ? eligibleResizeCorners(
            previewSize?.gridX ?? currentGridX ?? 0,
            previewSize?.gridY ?? currentGridY ?? 0,
            displaySpan,
            displayHeight,
        ).map((corner) => {
            const fromLeft = corner.endsWith('left');
            const fromTop = corner.startsWith('top');
            const active = isDragResizing && activeCorner === corner;
            return (
                <Box
                    key={corner}
                    data-resize-handle
                    aria-label={`Resize from ${corner.replace('-', ' ')}`}
                    position="absolute"
                    top={fromTop ? 0 : undefined}
                    bottom={fromTop ? undefined : 0}
                    left={fromLeft ? 0 : undefined}
                    right={fromLeft ? undefined : 0}
                    w="16px"
                    h="16px"
                    cursor={cornerCursor(corner)}
                    zIndex={3}
                    onPointerDown={handleResizePointerDown(corner)}
                    style={{ touchAction: 'none' }}
                    userSelect="none"
                >
                    <Box
                        position="absolute"
                        top={fromTop ? '2px' : undefined}
                        bottom={fromTop ? undefined : '2px'}
                        left={fromLeft ? '2px' : undefined}
                        right={fromLeft ? undefined : '2px'}
                        w="10px"
                        h="2px"
                        borderRadius="full"
                        bg="#4051B5"
                        opacity={active ? 0.9 : 0}
                        transition="opacity 0.12s"
                        _groupHover={{ opacity: 0.9 }}
                    />
                    <Box
                        position="absolute"
                        top={fromTop ? '2px' : undefined}
                        bottom={fromTop ? undefined : '2px'}
                        left={fromLeft ? '2px' : undefined}
                        right={fromLeft ? undefined : '2px'}
                        w="2px"
                        h="10px"
                        borderRadius="full"
                        bg="#4051B5"
                        opacity={active ? 0.9 : 0}
                        transition="opacity 0.12s"
                        _groupHover={{ opacity: 0.9 }}
                    />
                </Box>
            );
        })
        : null;

    const cardShell = (
        <Box
            data-widget-card
            role="group"
            position="relative"
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor={isMoving || isDragResizing ? '#4051B5' : 'gray.200'}
            shadow="sm"
            display="flex"
            flexDirection="column"
            h="100%"
            w="100%"
            overflow="hidden"
            cursor={canMove ? (isMoving ? 'grabbing' : 'grab') : undefined}
            onPointerDown={canMove ? onMovePointerDown : undefined}
            opacity={isMoving ? 0.55 : 1}
            boxShadow={isMoving || isDragResizing ? '0 0 0 1px #4051B5' : undefined}
            _hover={canMove ? { borderColor: '#4051B5', boxShadow: '0 0 0 1px #4051B5' } : undefined}
            transition={isMoving || isDragResizing ? 'none' : 'border-color 0.12s, box-shadow 0.12s, opacity 0.12s'}
        >
            <Box
                px={4}
                py={2.5}
                flexShrink={0}
                bg="gray.50"
                style={canMove ? { touchAction: 'none' } : undefined}
            >
                {cardHeader}
            </Box>
            {chartBody}
            {resizeHandles}
        </Box>
    );

    return (
        <ReportingChartFullscreenProvider value={isFullScreen}>
            <ReportingFullScreenShell
                isOpen={isFullScreen}
                onClose={() => setIsFullScreen(false)}
                fullscreenToolbar={isFullScreen ? cardHeader : undefined}
            >
                {isFullScreen ? (
                    <Box flex={1} minH={0} display="flex" flexDirection="column" w="100%" h="100%">
                        {chartBody}
                    </Box>
                ) : (
                    cardShell
                )}
            </ReportingFullScreenShell>
        </ReportingChartFullscreenProvider>
    );
};

export default WidgetCard;